import { useCallback, useEffect, useState } from 'react';
import { useApp } from '@/components/hooks/useApp';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { safeStorage } from '@/lib/safeStorage';

// Surface style — "classic" (default) | "liquid".
//
// Ortogonal al modo light/dark (que vive en la clase .dark de next-themes):
// se refleja en <html data-surface="..."> igual que la densidad usa
// <html data-density>. Todo el CSS del tema Liquid está scoped a
// html[data-surface="liquid"], así que sin el atributo la app se ve idéntica
// al tema clásico (riesgo cero para tenants que no opten).
//
// Prioridad: preferencia por-usuario > default del tenant (company.theme.surface)
// > "classic". localStorage actúa como caché síncrona para aplicar el estilo en
// el primer paint sin esperar a la red; users.preferences.surface es la fuente
// de verdad cross-device (PreferencesShape en /api/users/me/preferences).

const STORAGE_KEY = 'verix_surface';
const VALID = new Set(['classic', 'liquid']);
const DEFAULT_SURFACE = 'classic';

function readUserPref() {
  const raw = safeStorage.getItem(STORAGE_KEY);
  return VALID.has(raw) ? raw : null;
}

export function useSurfaceStyle() {
  const { company } = useApp();
  const { preferences, update: updatePrefs } = useUserPreferences();
  const tenantDefault = VALID.has(company?.theme?.surface)
    ? company.theme.surface
    : DEFAULT_SURFACE;

  const [userPref, setUserPref] = useState(() => readUserPref());

  // Cross-device hydration: el backend gana sobre un caché local posiblemente
  // obsoleto (p.ej. fijado en otro dispositivo). Se refleja en localStorage para
  // que los siguientes primeros paints sean instantáneos.
  const backendSurface = VALID.has(preferences?.surface) ? preferences.surface : null;
  useEffect(() => {
    if (backendSurface && backendSurface !== userPref) {
      safeStorage.setItem(STORAGE_KEY, backendSurface);
      setUserPref(backendSurface);
    }
  }, [backendSurface, userPref]);

  // El usuario manda siempre sobre el default del tenant.
  const surface = userPref || tenantDefault;

  // Reflejar en <html data-surface> para que el CSS Liquid pueda ramificar.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-surface', surface);
  }, [surface]);

  const setSurface = useCallback((next) => {
    if (!VALID.has(next)) return;
    safeStorage.setItem(STORAGE_KEY, next);
    setUserPref(next);
    updatePrefs({ surface: next }); // write-through a la fuente de verdad cross-device
  }, [updatePrefs]);

  const clearOverride = useCallback(() => {
    safeStorage.removeItem(STORAGE_KEY);
    setUserPref(null);
    // Empuja el default del tenant al backend para que la hidratación cross-device
    // no re-aplique el override anterior en el siguiente render.
    updatePrefs({ surface: tenantDefault });
  }, [updatePrefs, tenantDefault]);

  return {
    surface,
    setSurface,
    clearOverride,
    isOverridden: userPref !== null && userPref !== tenantDefault,
    tenantDefault,
  };
}

export const __test = { STORAGE_KEY, VALID, DEFAULT_SURFACE };
